import * as React from 'react';
import { Link } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import Button from '../Button/Button';

export type OnboardingStep = {
  done: boolean;
  title: string;
  description: string;
  cta: string;
  to: string;
};

type OnboardingStepperProps = {
  title?: string;
  subtitle?: string;
  steps: OnboardingStep[];
};

const CheckIcon: React.FC = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='3'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <polyline points='20 6 9 17 4 12' />
  </svg>
);

const StepIndicator: React.FC<{ done: boolean; index: number }> = ({ done, index }) => (
  <Box
    aria-hidden
    sx={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: done ? '#1a1a1a' : 'transparent',
      border: done ? 'none' : '1.5px solid #C9C9C9',
      color: done ? '#ffffff' : '#6B6B6B',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 15,
      fontWeight: 600,
      transition: 'background-color 200ms ease, border-color 200ms ease',
    }}
  >
    {done ? <CheckIcon /> : index}
  </Box>
);

export const OnboardingStepper: React.FC<OnboardingStepperProps> = ({
  title = 'Get started',
  subtitle = 'Complete these steps to start receiving promotions',
  steps,
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #E5E5E5',
        borderRadius: 2,
        backgroundColor: '#FAFAFA',
        boxShadow:
          '0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 600, lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: '13px', color: '#6B6B6B', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <Stack divider={<Box sx={{ borderTop: '1px solid #ECECEC' }} />}>
        {steps.map((step, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 3,
              py: 2.25,
            }}
          >
            <StepIndicator done={step.done} index={i + 1} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  textDecoration: step.done ? 'line-through' : 'none',
                  opacity: step.done ? 0.55 : 1,
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#6B6B6B',
                  lineHeight: 1.4,
                  mt: 0.25,
                  opacity: step.done ? 0.7 : 1,
                }}
              >
                {step.description}
              </Typography>
            </Box>

            <Button
              component={Link}
              to={step.to}
              variant='outlined'
              size='small'
              disabled={step.done}
              sx={{
                flexShrink: 0,
                minWidth: 110,
                whiteSpace: 'nowrap',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1a1a1a',
                borderColor: '#C9C9C9',
                backgroundColor: '#ffffff',
                '&:hover': {
                  borderColor: '#1a1a1a',
                  backgroundColor: '#fafafa',
                },
                '&.Mui-disabled': {
                  borderColor: '#ECECEC',
                  color: '#9E9E9E',
                },
              }}
            >
              {step.cta}
            </Button>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default OnboardingStepper;
